import { NEWS, NEWS_TAGS, type NewsEntry, type NewsTag } from '@somnhold/content';
import { getLang, t } from './i18n.ts';
import { clear, h } from './ui/dom.ts';
import { ICONS } from './ui/icons.ts';

/**
 * The news board: every release, newest first, with what it added, changed, rebalanced and fixed.
 * What this viewer hasn't opened yet is marked (a per-browser convenience, kept in localStorage).
 */

const SEEN_KEY = 'somnhold.news.seen';
const latest = (): string => NEWS[0]?.id ?? '';

const readSeen = (): string => {
  try {
    return localStorage.getItem(SEEN_KEY) ?? '';
  } catch {
    return latest();
  }
};
const markSeen = (): void => {
  try {
    localStorage.setItem(SEEN_KEY, latest());
  } catch {
    /* storage blocked: nothing is marked */
  }
  for (const f of listeners) f();
};

/** Releases this viewer hasn't seen (ids sort as dates). A first visit counts only the newest one. */
export const unseenNews = (): number => {
  const seen = readSeen();
  if (!seen) return NEWS.length ? 1 : 0;
  return NEWS.filter((e) => e.id > seen).length;
};

const listeners = new Set<() => void>();
/** Called when the news are read, so dots elsewhere can go out. */
export const onNewsSeen = (f: () => void): (() => void) => {
  listeners.add(f);
  return () => listeners.delete(f);
};

const TAG_ICON: Record<NewsTag, string> = { new: ICONS.sparkle, improved: ICONS.hammer, balance: ICONS.oath, fixed: ICONS.repair };

const fmtDate = (id: string): { day: string; year: string } => {
  const [y, m, d] = id.slice(0, 10).split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  const loc = getLang() === 'es' ? 'es-AR' : 'en-US';
  return { day: date.toLocaleDateString(loc, { day: 'numeric', month: 'short', timeZone: 'UTC' }).replace('.', ''), year: String(y) };
};

const entryView = (e: NewsEntry, filter: NewsTag | 'all', unseen: boolean): HTMLElement | null => {
  const items = e.items.filter((i) => filter === 'all' || i.tag === filter);
  if (!items.length) return null;
  const { day, year } = fmtDate(e.id);
  const lead = filter === 'all' && e.lead ? items.find((i) => i.id === e.lead) : undefined;
  const rest = items.filter((i) => i !== lead);
  const row = (i: (typeof items)[number]): HTMLElement =>
    h(
      `li.news-item.t-${i.tag}`,
      {},
      h('span.news-tag', {}, h('span', { html: TAG_ICON[i.tag] }), t(`news.tag.${i.tag}`)),
      h('div.news-text', {}, h('b', {}, t(`news.${e.id}.${i.id}`)), h('p', {}, t(`news.${e.id}.${i.id}.desc`))),
    );
  return h(
    `article.news-entry${unseen ? '.unseen' : ''}`,
    { 'data-news': e.id },
    h('div.news-date', {}, h('b', {}, day), h('span', {}, year)),
    h(
      'div.news-main',
      {},
      h('div.news-head', {}, h('h3', {}, t(`news.${e.id}.title`)), unseen ? h('span.news-unseen', {}, t('news.unseen')) : '', h('span.news-count', {}, t('news.count', { n: items.length }))),
      filter === 'all' ? h('p.news-intro', {}, t(`news.${e.id}.intro`)) : '',
      lead
        ? h(
            `div.news-lead.t-${lead.tag}`,
            {},
            h('span.news-lead-ico', { html: TAG_ICON[lead.tag] }),
            h('div', {}, h('span.news-tag', {}, t(`news.tag.${lead.tag}`)), h('b', {}, t(`news.${e.id}.${lead.id}`)), h('p', {}, t(`news.${e.id}.${lead.id}.desc`))),
          )
        : '',
      rest.length ? h('ul.news-list', {}, ...rest.map(row)) : '',
    ),
  );
};

/** Opens the board over whatever is on screen; reading it marks everything seen. */
export const newsModal = (): void => {
  const seen = readSeen();
  let filter: NewsTag | 'all' = 'all';
  const body = h('div.body.news-body');
  const chips = h('div.news-filter', { role: 'tablist', 'aria-label': t('news.title') });
  const render = (): void => {
    clear(chips);
    for (const f of ['all', ...NEWS_TAGS] as const) {
      const n = f === 'all' ? NEWS.reduce((a, e) => a + e.items.length, 0) : NEWS.reduce((a, e) => a + e.items.filter((i) => i.tag === f).length, 0);
      chips.append(
        h(
          `button.news-chip${filter === f ? '.on' : ''}${f === 'all' ? '' : `.t-${f}`}`,
          { role: 'tab', 'aria-selected': String(filter === f), 'data-filter': f, onclick: () => { filter = f; render(); body.scrollTop = 0; } },
          f === 'all' ? t('news.all') : t(`news.tag.${f}`),
          h('span', {}, String(n)),
        ),
      );
    }
    clear(body);
    // Unread: newer than the last release seen (on a first visit, just the newest one).
    const unseen = (e: NewsEntry): boolean => (seen ? e.id > seen : e.id === latest());
    const views = NEWS.map((e) => entryView(e, filter, unseen(e))).filter((x): x is HTMLElement => !!x);
    if (views.length) body.append(...views);
    else body.append(h('p.small.hint', {}, t('news.empty')));
  };
  render();
  const close = (): void => {
    back.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') close();
  };
  const back = h(
    'div.modal-back.news-back',
    { onclick: (ev: Event) => { if (ev.target === back) close(); } },
    h(
      'div.modal.news',
      { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'news-h', 'data-test': 'news' },
      h('header', {}, h('div.news-title', {}, h('span.news-ico', { html: ICONS.news }), h('div', {}, h('h2', { id: 'news-h' }, t('news.title')), h('p', {}, t('news.sub')))), chips),
      body,
      h('footer', {}, h('button.btn', { onclick: close }, t('news.close'))),
    ),
  );
  document.addEventListener('keydown', onKey);
  document.body.append(back);
  markSeen();
};

/** A «What's new» button with a dot while something is unread. */
export const newsButton = (cls: string, withLabel = true): HTMLElement => {
  const dot = h('span.news-dot', { 'aria-hidden': 'true' });
  const btn = h(`button.${cls}`, { 'data-test': 'news-btn', 'aria-label': t('news.open'), title: t('news.open'), onclick: () => newsModal() }, h('span.news-btn-ico', { html: ICONS.news }, dot), withLabel ? h('span.lbl', {}, t('news.open')) : '');
  btn.classList.toggle('has-news', unseenNews() > 0);
  // Held weakly: the button leaves with its screen, and a listener holding it kept the whole screen alive.
  const ref = new WeakRef(btn);
  const off = onNewsSeen(() => {
    const b = ref.deref();
    if (b) b.classList.toggle('has-news', unseenNews() > 0);
    else off();
  });
  return btn;
};
